import type { RecipeV2Doc } from '@ctcells/api'

/**
 * 「표준 레시피에서」 순수 조각(2026-10-06) — 화면 파일이 컴포넌트만 내보내게 여기로 뺐다(빠른 새로고침).
 */

/** 문서 이름 — `metadata.protocolName`(Opentrons protocol/8). 없으면 빈칸 */
export function protocolNameOf(doc: RecipeV2Doc | undefined): string {
  const meta = doc?.['metadata']
  if (typeof meta !== 'object' || meta === null) return ''
  const name = (meta as Record<string, unknown>)['protocolName']
  return typeof name === 'string' ? name : ''
}

/**
 * 서버가 준 칸 하나를 글자로 — 글자 · 수만 그대로, 그 밖(객체 · 없음)은 `fallback`.
 * 🔴 `String(객체)` 로 「[object Object]」를 찍지 않는다.
 */
export function scalarText(value: unknown, fallback: string): string {
  if (typeof value === 'string') return value
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  return fallback
}

/**
 * 저장 때 보낼 판(`schema_version`) — 문서가 스스로 밝힌 `$otSharedSchema`, 없으면 `schemaVersion`. 둘 다 없으면 빈칸
 * (서버가 빈 값을 422 로 막는다 — 화면이 판을 짓지 않는다).
 */
export function declaredSchemaVersion(doc: RecipeV2Doc): string {
  const shared = scalarText(doc['$otSharedSchema'], '')
  return shared !== '' ? shared : scalarText(doc['schemaVersion'], '')
}

/**
 * 새 형식(Opentrons protocol/8) 레시피인가 — 서버 판정(`recipe_v2/doc.is_new_format`)과 같은 규칙:
 * 최상위에 `$otSharedSchema` 또는 `designerApplication` 이 있으면 새 형식이다. 🔴 판정이 두 벌이면 조용히 어긋난다 — 이 하나만 쓴다.
 */
export function isNewFormatRecipe(payload: unknown): boolean {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    ('$otSharedSchema' in payload || 'designerApplication' in payload)
  )
}

/**
 * 새 형식 레시피의 요약 — **문서에 실제로 있는 값만**. 칸이 없거나 모양이 다르면 그 값은 `undefined`(화면이 줄째 뺀다).
 * 🔴 옛 형식 요약(절 · 스텝 · 원심/어셈/염색 수)은 새 형식에서 «0»을 그려 «없다»로 읽혔다(2026-10-06 dev2 실측) — 그 자리를 대신한다.
 */
export interface NewFormatFacts {
  /** `schemaVersion`(예 8) */
  schemaVersion?: string | undefined
  /** `metadata.protocolName` */
  protocolName?: string | undefined
  /** `commands` 길이 */
  commands?: number | undefined
  /** `labwareDefinitions` 정의 수 */
  labwareDefinitions?: number | undefined
  /** `liquids` 수 */
  liquids?: number | undefined
}

const countOf = (value: unknown): number | undefined =>
  Array.isArray(value)
    ? value.length
    : typeof value === 'object' && value !== null
      ? Object.keys(value).length
      : undefined

export function newFormatFacts(payload: unknown): NewFormatFacts {
  if (typeof payload !== 'object' || payload === null) return {}
  const doc = payload as Record<string, unknown>
  const version = scalarText(doc['schemaVersion'], '')
  const name = protocolNameOf(doc)
  const commands: unknown = doc['commands']
  const labware = countOf(doc['labwareDefinitions'])
  const liquids = countOf(doc['liquids'])
  return {
    ...(version === '' ? {} : { schemaVersion: version }),
    ...(name === '' ? {} : { protocolName: name }),
    ...(Array.isArray(commands) ? { commands: commands.length } : {}),
    ...(labware === undefined ? {} : { labwareDefinitions: labware }),
    ...(liquids === undefined ? {} : { liquids }),
  }
}
